/**
 * Pointer and keyboard auto-tracking for the element-id click convention.
 *
 * The two paths cannot be independent listeners. A custom ARIA control commonly handles keydown by
 * calling `.click()` on itself, so a naive pair of listeners records one activation twice; and a
 * `role="button"` with no Space handler records an activation that never happened. Both are worse
 * than a gap, because the event name stays valid while the count stops meaning anything.
 *
 * So the keyboard path resolves its decision after the event has finished dispatching, when two facts
 * it needs are finally knowable: whether a click was already tracked for the same control, and
 * whether anything called `preventDefault()`.
 */

import { getAutoTrackedClick, getKeyboardActivatedClick, keyboardActivationNeedsProof } from '../directives/analytics.directives'
import type { AnalyticsEventProperties } from '../types/properties'

export type AutoClickTrackFn = (event: string, properties: AnalyticsEventProperties) => void

export interface AutoClickTracking {
  handleClick: (event: MouseEvent) => void
  handleKeyboardActivation: (event: KeyboardEvent) => void
}

/**
 * Schedulers are injectable so tests can drive the deferral deterministically instead of waiting.
 * The defaults matter: `queueMicrotask` runs after the event dispatch task and therefore after any
 * click synthesised inside a keydown handler, while `setTimeout` clears the dedupe set only after
 * every microtask queued during that task has already read it.
 */
export interface AutoClickTrackingSchedulers {
  deferDecision: (callback: () => void) => void
  scheduleReset: (callback: () => void) => void
}

const defaultSchedulers: AutoClickTrackingSchedulers = {
  deferDecision: callback => queueMicrotask(callback),
  scheduleReset: callback => {
    setTimeout(callback, 0)
  },
}

export function createAutoClickTracking(
  track: AutoClickTrackFn,
  schedulers: AutoClickTrackingSchedulers = defaultSchedulers
): AutoClickTracking {
  /**
   * Element ids tracked through the click path during the current task. Keyed by id rather than node
   * identity because a synthesised click can surface on a descendant of the keydown target while
   * still resolving to the same control.
   */
  const clickedElementIds = new Set<string>()
  let resetScheduled = false

  const rememberClickedElement = (elementId: string): void => {
    clickedElementIds.add(elementId)
    if (resetScheduled) return
    resetScheduled = true
    schedulers.scheduleReset(() => {
      clickedElementIds.clear()
      resetScheduled = false
    })
  }

  const handleClick = (event: MouseEvent): void => {
    const clickTracking = getAutoTrackedClick(event.target as HTMLElement)
    if (!clickTracking) return
    // Dedupe is keyed by id; clicks without an id cannot collide with a later keyboard activation
    // of a different control, so only remember when an id is present.
    const elementId = clickTracking.properties.elementId
    if (typeof elementId === 'string') rememberClickedElement(elementId)
    track(clickTracking.event, clickTracking.properties)
  }

  const handleKeyboardActivation = (event: KeyboardEvent): void => {
    const clickTracking = getKeyboardActivatedClick(event)
    if (!clickTracking) return

    const needsProof = keyboardActivationNeedsProof(event)
    const elementId = clickTracking.properties.elementId

    schedulers.deferDecision(() => {
      // The control turned this key press into a click, which the click path already counted.
      if (typeof elementId === 'string' && clickedElementIds.has(elementId)) return
      // Space that nothing prevented scrolled the page instead of activating the control.
      if (needsProof && !event.defaultPrevented) return
      track(clickTracking.event, clickTracking.properties)
    })
  }

  return { handleClick, handleKeyboardActivation }
}
